import { Beef, ChevronLeft, ChevronRight, Flame, Pause, Play, Salad, Wheat } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useState } from "react";
import { ABOUT_SLIDES } from "../../data/content";
import { cn, unsplash } from "../../utils/format";
import { Button } from "../ui/Button";
import { Reveal } from "../ui/Reveal";

const AUTOPLAY_MS = 10000;

const FEATURES = [
  { icon: Beef, label: "Blend preparado diariamente" },
  { icon: Wheat, label: "Pão brioche artesanal" },
  { icon: Salad, label: "Vegetais frescos do dia" },
  { icon: Flame, label: "Molhos autorais" },
];

const roundBtn =
  "grid size-11 place-items-center rounded-full border border-line text-white transition-colors hover:border-primary hover:text-primary";

export const SobreNos = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovering, setHovering] = useState(false);
  const slide = ABOUT_SLIDES[index];

  const nextSlide = useCallback(() => setIndex((prev) => (prev + 1) % ABOUT_SLIDES.length), []);
  const prevSlide = () => setIndex((prev) => (prev - 1 + ABOUT_SLIDES.length) % ABOUT_SLIDES.length);

  // Autoplay de 10s (pausa no hover/foco ou pelo botão). Reinicia ao trocar de slide.
  useEffect(() => {
    if (paused || hovering) return;
    const intervalo = setInterval(nextSlide, AUTOPLAY_MS);
    return () => clearInterval(intervalo);
  }, [paused, hovering, index, nextSlide]);

  return (
    <section
      id="Sobre"
      aria-label="Sobre nós"
      aria-roledescription="carrossel"
      className="relative overflow-hidden bg-night"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onFocus={() => setHovering(true)}
      onBlur={() => setHovering(false)}
    >
      <div className="grid lg:min-h-[680px] lg:grid-cols-2">
        <div className="relative min-h-[340px] overflow-hidden sm:min-h-[420px]">
          <AnimatePresence initial={false}>
            <motion.img
              key={slide.img}
              src={unsplash(slide.img, 1200)}
              alt={`Hambúrguer artesanal Ninja Burger — ${slide.title}`}
              initial={{ opacity: 0, scale: 1.08 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
              className="absolute inset-0 size-full object-cover"
            />
          </AnimatePresence>
          <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-night via-night/10 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-night" />
          <span className="absolute bottom-6 left-6 rounded-full bg-night/70 px-4 py-1.5 font-display text-lg tracking-widest backdrop-blur">
            <span className="text-primary">0{index + 1}</span> / 0{ABOUT_SLIDES.length}
          </span>
        </div>

        <Reveal className="flex flex-col justify-center px-4 py-16 sm:px-10 lg:px-14 xl:px-20">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Sobre nós</p>
          <p className="mt-2 text-sm text-muted">Conheça a história e a qualidade por trás dos nossos burgers.</p>

          <div className="mt-6 min-h-[320px] sm:min-h-[280px]" aria-live={paused ? "polite" : "off"}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }}
              >
                <h2 className="font-display text-5xl leading-none tracking-wide sm:text-6xl">
                  {slide.title.split(" ").slice(0, -1).join(" ")}{" "}
                  <span className="text-primary">{slide.title.split(" ").slice(-1)}</span>
                </h2>
                <p className="mt-5 text-sm leading-relaxed text-muted sm:text-[15px]">{slide.text}</p>
              </motion.div>
            </AnimatePresence>
          </div>

          <ul className="mt-8 grid grid-cols-2 gap-x-4 gap-y-5 border-y border-line py-6 sm:grid-cols-4">
            {FEATURES.map(({ icon: Icon, label }) => (
              <li key={label} className="flex flex-col items-start gap-2 text-xs font-medium text-white/90">
                <Icon className="size-6 text-primary" aria-hidden="true" />
                {label}
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button to="/cardapio">Ver Cardápio</Button>
            <div className="ml-auto flex items-center gap-2">
              <button type="button" onClick={prevSlide} aria-label="Slide anterior" className={roundBtn}>
                <ChevronLeft className="size-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => setPaused((p) => !p)}
                aria-label={paused ? "Retomar troca automática" : "Pausar troca automática"}
                className={roundBtn}
              >
                {paused ? <Play className="size-4" aria-hidden="true" /> : <Pause className="size-4" aria-hidden="true" />}
              </button>
              <button type="button" onClick={nextSlide} aria-label="Próximo slide" className={roundBtn}>
                <ChevronRight className="size-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="mt-6 flex gap-1">
            {ABOUT_SLIDES.map((s, i) => (
              <button
                key={s.title}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Ir para o slide ${i + 1}: ${s.title}`}
                aria-current={i === index ? "true" : undefined}
                className="group p-1.5"
              >
                <span
                  className={cn(
                    "block size-2.5 rounded-full transition-[transform,background-color] duration-300",
                    i === index ? "scale-125 bg-primary" : "bg-line group-hover:bg-muted",
                  )}
                />
              </button>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
